import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Card, Table, Button, Badge, Spinner } from "react-bootstrap";
import Select from "react-select"; 
import axios from "axios";
import API_ENDPOINTS from "../../config/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AttendanceDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- 1. DATE LOGIC ---
  const getInitialDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    return { start: formatDate(firstDay), end: formatDate(lastDay) };
  };

  const dates = getInitialDates();
  const [filters, setFilters] = useState({ 
    startDate: dates.start, 
    endDate: dates.end, 
    selectedUser: null, 
    branchId: "" 
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // --- 2. EXCEL EXPORT LOGIC ---
  const exportToExcel = () => {
    if (logs.length === 0) {
      alert("No data available to export!");
      return;
    }

    let totalSum = 0;
    const excelData = logs.map((log) => {
      if (log.isTotalRow) {
        totalSum += log.total || 0;
        return {
          "ID": "",
          "EMPLOYEE NAME": "",
          "DATE": "",
          "ACTION": `--- TOTAL ${log.label} ---`,
          "TIME": "",
          "DURATION": `${log.total.toFixed(2)}h`,
          "LOCATION": "",
          "LATITUDE": "",
          "LONGITUDE": ""
        };
      } else {
        const dateObj = new Date(log.createdAt || log.CreatedAt);
        return {
          "ID": log.userId || log.UserId,
          "EMPLOYEE NAME": log.fullName || log.employeeName || log.EmployeeName || "N/A",
          "DATE": dateObj.toLocaleDateString('en-GB'),
          "ACTION": log.actionType || log.ActionType,
          "TIME": dateObj.toLocaleTimeString('en-GB', { hour12: false }),
          "DURATION": (log.durationHours || log.DurationHours) > 0 
                      ? `${(log.durationHours || log.DurationHours).toFixed(2)}h` 
                      : "-",
          "LOCATION": log.workLocation || log.WorkLocation,
          "LATITUDE": log.latitude ?? log.Latitude ?? "",
          "LONGITUDE": log.longitude ?? log.Longitude ?? ""
        };
      }
    });

    excelData.push({
      "ID": "", "EMPLOYEE NAME": "", "DATE": "",
      "ACTION": "=== GRAND TOTAL (SUM) ===",
      "TIME": "", "DURATION": `${totalSum.toFixed(2)}h`,
      "LOCATION": "", "LATITUDE": "", "LONGITUDE": ""
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AttendanceReport");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
    saveAs(data, `Attendance_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // --- 3. LOGIC XỬ LÝ DỮ LIỆU ---
  const processAttendancePairs = useCallback((rawData) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) return [];
    
    const finalDisplay = [];
    const userGroups = {};

    rawData.forEach(log => {
      const uId = log.userId || log.UserId;
      if (!userGroups[uId]) userGroups[uId] = { loginLogs: [], checkinLogs: [] };
      const action = (log.actionType || log.ActionType || "").toUpperCase();
      
      if (action.includes("LOGIN") || action.includes("LOGOUT")) {
        userGroups[uId].loginLogs.push(log);
      } else if (action.includes("CHECKIN") || action.includes("CHECKOUT")) {
        userGroups[uId].checkinLogs.push(log);
      }
    });

    const pairSequence = (list, label) => {
      const sorted = [...list].sort((a, b) => new Date(a.createdAt || a.CreatedAt) - new Date(b.createdAt || b.CreatedAt));
      const paired = [];
      let i = 0;
      while (i < sorted.length) {
        const current = sorted[i];
        const next = sorted[i + 1];
        const currentAction = (current.actionType || current.ActionType || "").toUpperCase();
        
        if (currentAction.includes("IN") && next && (next.actionType || next.ActionType || "").toUpperCase().includes("OUT")) {
          paired.push(current, next);
          paired.push({ isTotalRow: true, total: next.durationHours || next.DurationHours || 0, label });
          i += 2;
        } else {
          paired.push(current);
          paired.push({ isTotalRow: true, total: current.durationHours || current.DurationHours || 0, label: `${label} (Single)` });
          i += 1;
        }
      }
      return paired;
    };

    Object.values(userGroups).forEach(group => {
      if (group.loginLogs.length > 0) finalDisplay.push(...pairSequence(group.loginLogs, "LOGIN ➔ LOGOUT"));
      if (group.checkinLogs.length > 0) finalDisplay.push(...pairSequence(group.checkinLogs, "CHECKIN ➔ CHECKOUT"));
    });

    return finalDisplay;
  }, []);

  // --- 4. DATA FETCHING ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        branchId: filters.branchId,
        userId: filters.selectedUser ? filters.selectedUser.value : ""
      };

      const res = await axios.get(`${API_ENDPOINTS.attendance}/logs`, { 
        headers: getAuthHeader(), 
        params: params 
      });

      // Backend returns { attendance: [], leaves: [] }
      const attendanceData = (res.data && Array.isArray(res.data.attendance)) ? res.data.attendance : [];
      setLogs(processAttendancePairs(attendanceData));
      
    } catch (err) { 
      console.error("Load log error:", err); 
      setLogs([]);
    } finally { 
      setLoading(false); 
    }
  }, [filters, processAttendancePairs]);

  useEffect(() => {
    const fetchInit = async () => {
      try {
        const [bRes, uRes] = await Promise.all([
          axios.get(`${API_ENDPOINTS.url}/api${API_ENDPOINTS.branches}`, { headers: getAuthHeader() }),
          axios.get(API_ENDPOINTS.users, { headers: getAuthHeader() })
        ]);
        setBranches(bRes.data || []);
        setUsers(uRes.data || []);
      } catch (e) { console.error("Init error:", e); }
    };
    fetchInit();
    fetchData();
  }, [fetchData]);

  const grandTotal = useMemo(() => {
    return logs.filter(l => l.isTotalRow).reduce((sum, l) => sum + (l.total || 0), 0);
  }, [logs]);

  const userOptions = users.map(u => ({
    value: u.id || u.Id,
    label: u.fullName || u.employeeName || u.userName || u.UserName
  }));

  const selectStyles = {
    control: (base) => ({ 
      ...base, 
      borderRadius: "20px", 
      fontSize: "0.85rem", 
      minHeight: "31px",
      borderColor: "#dee2e6",
      boxShadow: "none"
    })
  };

  return (
    <div className="p-4 bg-white min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold text-dark text-uppercase m-0">Attendance System</h4>
        <div className="d-flex gap-2">
           <Button variant="outline-secondary" size="sm" className="fw-bold" onClick={() => fetchData()}>REFRESH</Button>
           <Button variant="success" size="sm" className="fw-bold" onClick={exportToExcel}>EXPORT EXCEL</Button>
        </div>
      </div>

      <Card className="mb-4 border-0 bg-light shadow-sm p-3">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <label className="small fw-bold text-muted">START DATE</label>
            <input type="date" className="form-control form-control-sm" style={{borderRadius: "20px"}} value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} />
          </Col>
          <Col md={2}>
            <label className="small fw-bold text-muted">END DATE</label>
            <input type="date" className="form-control form-control-sm" style={{borderRadius: "20px"}} value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} />
          </Col>
          <Col md={2}>
            <label className="small fw-bold text-muted">BRANCH</label>
            <select className="form-select form-select-sm" style={{borderRadius: "20px"}} value={filters.branchId} onChange={e => setFilters({...filters, branchId: e.target.value})}>
              <option value="">-- All --</option>
              {branches.map(b => <option key={b.id || b.Id} value={b.id || b.Id}>{b.branchName || b.BranchName}</option>)}
            </select>
          </Col>
          
          <Col md={4}>
            <label className="small fw-bold text-muted">FULL NAME</label>
            <Select
              isClearable
              options={userOptions}
              value={filters.selectedUser}
              onChange={(option) => setFilters({ ...filters, selectedUser: option })}
              placeholder="Search employee..."
              styles={selectStyles}
              classNamePrefix="select"
            />
          </Col>

          <Col md={2}>
            <Button variant="dark" className="w-100 fw-bold btn-sm rounded-pill" onClick={fetchData}>SEARCH</Button>
          </Col>
        </Row>
      </Card>

      <Table responsive bordered hover className="shadow-sm bg-white">
        <thead className="text-center">
          <tr style={{ backgroundColor: "#4e73df", color: "white" }}>
            <th className="bg-primary text-white">ID</th>
            <th className="bg-primary text-white">FULL NAME</th>
            <th className="bg-primary text-white">DATE</th>
            <th className="bg-primary text-white">ACTION</th>
            <th className="bg-primary text-white">TIME</th>
            <th className="bg-primary text-white">HOURS</th>
            <th className="bg-primary text-white">LOCATION</th>
            <th className="bg-primary text-white">LAT</th>
            <th className="bg-primary text-white">LNG</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan="9" className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
          ) : logs.length === 0 ? (
            <tr><td colSpan="9" className="text-center py-4">No data found</td></tr>
          ) : (
            logs.map((log, i) => (
              log.isTotalRow ? (
                <tr key={i} className="bg-light fw-bold">
                  <td colSpan="5" className="text-end text-uppercase">Total {log.label}</td>
                  <td className="text-center text-danger">{log.total.toFixed(2)}h</td>
                  <td colSpan="3"></td>
                </tr>
              ) : (
                <tr key={i}>
                  <td>{log.userId || log.UserId}</td>
                  <td className="fw-bold text-primary">{log.fullName || log.employeeName || log.EmployeeName}</td>
                  <td>{new Date(log.createdAt || log.CreatedAt).toLocaleDateString('en-GB')}</td>
                  <td className="text-center">
                    <Badge bg={(log.actionType || log.ActionType || "").toUpperCase().includes("IN") ? "primary" : "secondary"}>
                      {log.actionType || log.ActionType}
                    </Badge>
                  </td>
                  <td>{new Date(log.createdAt || log.CreatedAt).toLocaleTimeString('en-GB', { hour12: false })}</td>
                  <td className="text-center">{log.durationHours || log.DurationHours || "-"}</td>
                  <td><small>{log.workLocation || log.WorkLocation}</small></td>
                  <td><small>{log.latitude ?? log.Latitude ?? ""}</small></td>
                  <td><small>{log.longitude ?? log.Longitude ?? ""}</small></td>
                </tr>
              )
            ))
          )}
          {!loading && logs.length > 0 && (
            <tr className="table-warning fw-bold">
              <td colSpan="5" className="text-end">GRAND TOTAL (SUM)</td>
              <td className="text-center text-danger">{grandTotal.toFixed(2)}h</td>
              <td colSpan="3"></td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default AttendanceDashboard;